import { Sites } from '@ps/platform-message-framework/common/Sites';
import { DeAddressVerificationResult, ErrorCode, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { styles as sharedStyles } from '@swf/account';
import { select } from '@swf/core/store';
import type { Response } from '@swf/core/types/Account';
import { encodeQuery, scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';

import ErrorResponseMessage from './ErrorResponseMessage/ErrorResponseMessage';
import {
  TXTCLI_Something_went_wrongX_please_t_ELL,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format,
  TXTWCL_The_city_you_have_entered_is_i_ELL_1
} from './useAddressUpdate.messages.json';

export interface AddressFormValues {
  streetAddress?: string;
  city?: string;
  state: string;
  zip: string;
  country: string;
  streetName?: string;
  buildingNumber?: string;
  orientationNumber?: string;
  region?: string;
  district?: string;
  partOfMunicipality?: string;
  extAuthToken?: string;
  fullName?: string;
  [key: string]: string | undefined;
}

const errorTranslations = {
  TXTWCL_The_city_you_have_entered_is_i_ELL_1,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format
};

export const useAddressUpdate = (
  onUpdate: (formValues: AddressFormValues) => Promise<Response | void>,
  successCallback?: () => void
) => {
  const site = useSelector(select.cms.getSiteId);
  const country = useSelector(select.account.getCountry) as string;
  const euDeOasisStreetAddress = useFeature(FeatureFlag.EuDeOasisStreetAddress);
  const { push } = useHistory();
  const isSiteCZ = site === Sites.CZE;

  return (values: AddressFormValues) =>
    onUpdate({
      ...values,
      country,
      streetAddress:
        euDeOasisStreetAddress || isSiteCZ
          ? `${values.streetName} ${values.buildingNumber}`
          : values.streetAddress
    }).then((response) => {
      scrollToTop();

      if (response) {
        if (response.errors?.beError) {
          push({
            pathname: '/notifications/egamesng-notification',
            search: encodeQuery({ belgianRegulatorError: response.errors.beError })
          });
          return;
        }

        const errors = buildFormErrors(response, errorTranslations);

        if (response.deAddressVerificationResult === DeAddressVerificationResult.Failed) {
          return { ...errors, [FORM_ERROR]: <ErrorResponseMessage /> };
        } else if (response.errors?.code === ErrorCode.ERR_LUGAS_REQUEST_FAILED) {
          return {
            [FORM_ERROR]: (
              <StarsInfo type="error" className={sharedStyles.message}>
                <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />{' '}
              </StarsInfo>
            )
          };
        } else if (errors?.[FORM_ERROR]) {
          return {
            ...errors,
            [FORM_ERROR]: (
              <StarsInfo type="error" className={sharedStyles.message}>
                {errors?.[FORM_ERROR]}
              </StarsInfo>
            )
          };
        } else if (successCallback && !errors) {
          successCallback();
          return;
        }

        return errors;
      }
    });
};
